import dayjs from "dayjs";

import { apiFormat, shortMonthAndYear } from "@similarweb/pro-logic-components";
import { type StrictColor, colorsPalettes } from "@similarweb/styles";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { InfoTooltip } from "app/components/React/Tooltip/InfoTooltip/InfoTooltip";
import { abbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import { METRIC_CARDS_KEY_PREFIX } from "../shared/constants";
import { MetricCard } from "../shared/MetricCard";
import { scrollToKeywordsBreakdown } from "../shared/scrollToKeywordsBreakdown";

import { useTotalSearchesCard } from "./useTotalSearchesCard";

const KEY_PREFIX = `${METRIC_CARDS_KEY_PREFIX}.total.searches`;

const formatDate = (date: string): string => (date ? dayjs(date, apiFormat).format(shortMonthAndYear) : "");

const getChangeColor = (change: number): StrictColor => {
    if (change > 0) {
        return colorsPalettes.green["400_P"];
    }
    if (change < 0) {
        return colorsPalettes.red["400_P"];
    }
    return colorsPalettes.carbon[400];
};

const TotalSearchesSkeleton = () => (
    <Stack
        flex="1"
        padding="24px"
        gap="14px"
        justifyContent="center"
        alignItems="center"
    >
        <PixelPlaceholderLoader
            width={100}
            height={56}
            borderRadius="6px"
        />
        <PixelPlaceholderLoader
            width={200}
            height={20}
            borderRadius="4px"
        />
        <PixelPlaceholderLoader
            width={200}
            height={20}
            borderRadius="4px"
        />
    </Stack>
);

export const TotalSearchesCard = () => {
    const t = useTranslation();
    const { data, isLoading, isError } = useTotalSearchesCard();

    return (
        <MetricCard
            title={t(`${KEY_PREFIX}.title`)}
            infoText={t(`${KEY_PREFIX}.tooltip`)}
            dataAutomation="search-trends-metric-card-total-searches"
            isLoading={isLoading}
            isEmpty={isError || !data}
            loadingTemplate={<TotalSearchesSkeleton />}
        >
            {data ? (
                <Stack
                    flex="1"
                    gap="4px"
                    padding="24px"
                    justifyContent="center"
                    alignItems="center"
                >
                    <Text
                        data-automation="search-trends-total-searches-volume"
                        fontVariant="h2"
                        color={colorsPalettes.carbon[500]}
                    >
                        {abbrNumberFilter()(data.value)}
                    </Text>
                    <Flex
                        gap="8px"
                        alignItems="center"
                    >
                        <Text
                            data-automation="search-trends-total-searches-change"
                            fontVariant="input2"
                            color={getChangeColor(data.change)}
                        >
                            {percentageSignFilter()(data.change, 2)}
                        </Text>
                        <Text
                            data-automation="search-trends-total-searches-duration-range"
                            fontVariant="body2"
                            color={colorsPalettes.carbon[300]}
                        >
                            {`${formatDate(data.dateRange.from)} - ${formatDate(data.dateRange.to)}`}
                        </Text>
                    </Flex>
                    <Flex
                        gap="4px"
                        alignItems="center"
                        padding="14px"
                    >
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon[500]}
                        >
                            {t(`${KEY_PREFIX}.based.on.prefix`)}
                        </Text>
                        <Text
                            as="button"
                            fontVariant="body2"
                            color={colorsPalettes.blue[400]}
                            background="none"
                            border="none"
                            padding="0"
                            cursor="pointer"
                            onClick={scrollToKeywordsBreakdown}
                            data-automation="search-trends-total-searches-keywords-link"
                        >
                            {t(`${KEY_PREFIX}.based.on.link`, { count: data.keywordCount.toLocaleString() })}
                        </Text>
                        <InfoTooltip infoText={t(`${KEY_PREFIX}.based.on.tooltip`)} />
                    </Flex>
                </Stack>
            ) : null}
        </MetricCard>
    );
};
